import { useEffect, useState } from "react"

import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import { getProjectSubcontractors } from "../../Project/api/project-subcontractors.services.ts"
import type { ContactRef } from "../types/compose.types.ts"

/** The lead or project whose Communication tab or header is showing. */
export interface CommRecord {
  type: "lead" | "project"
  id: string
  label: string
}

export type ContactRole = "customer" | "lead" | "subcontractor" | "supplier"

export interface CommContact {
  ref: ContactRef
  name: string
  role: ContactRole
}

export const contactKey = (ref: ContactRef) => `${ref.type}:${ref.id}`

interface PersonLike {
  id?: string
  first_name?: string | null
  last_name?: string | null
  middle_name?: string | null
  name?: string | null
}

/**
 * The record's own contact: its customer, or the lead itself while no
 * customer is linked (a lead's consent is then kept on the lead).
 */
export const primaryContactOf = (record: {
  id: string
  customer?: PersonLike | null
  temporary_customer?: PersonLike | null
  lead_name?: string | null
}): CommContact =>
  record.customer?.id
    ? {
        ref: { type: "customer", id: record.customer.id },
        name: getEntityName(record.customer, ""),
        role: "customer"
      }
    : {
        ref: { type: "lead", id: record.id },
        name:
          getEntityName(record.temporary_customer, "") ||
          record.lead_name ||
          "",
        role: "lead"
      }

// `responsible_type` on a project's finance parties: 2 is a subcontractor, 1 a supplier.
const PARTY_ROLES: Record<string, ContactRole> = {
  "2": "subcontractor",
  "1": "supplier"
}

const PARTY_PAGE_SIZE = 100

/**
 * The subcontractors and suppliers a project's lines name, the only other
 * people a project holds. A caller who cannot read the project's finances
 * gets none, which leaves the customer alone.
 */
export const loadProjectParties = async (
  projectId: string
): Promise<CommContact[]> => {
  const response = await getProjectSubcontractors({
    project_id: projectId,
    page_size: PARTY_PAGE_SIZE
  } as never)
  if (!response.ok || !response.data) return []
  const seen = new Set<string>()
  const parties: CommContact[] = []
  for (const row of response.data.results ?? []) {
    const role = PARTY_ROLES[String(row.responsible_type ?? "")]
    if (!role || !row.id) continue
    const ref = { type: role, id: row.id } as ContactRef
    if (seen.has(contactKey(ref))) continue
    seen.add(contactKey(ref))
    parties.push({ ref, name: row.full_name, role })
  }
  return parties
}

const partiesCache = new Map<string, Promise<CommContact[]>>()

/** Forgets every cached party list, for tests. */
export const resetRecordContactsCache = () => partiesCache.clear()

/**
 * Everyone this record can be written to: its own contact first, then a
 * project's subcontractors and suppliers. A lead only ever has the first.
 */
export const useRecordContacts = (
  record: Pick<CommRecord, "type" | "id">,
  primary: CommContact
) => {
  const [parties, setParties] = useState<CommContact[]>([])
  const [loading, setLoading] = useState(record.type === "project")

  useEffect(() => {
    if (record.type !== "project") {
      setParties([])
      setLoading(false)
      return
    }
    let current = true
    setLoading(true)
    let pending = partiesCache.get(record.id)
    if (!pending) {
      pending = loadProjectParties(record.id).catch(() => [])
      partiesCache.set(record.id, pending)
    }
    void pending.then((found) => {
      if (!current) return
      setParties(found)
      setLoading(false)
    })
    return () => {
      current = false
    }
  }, [record.type, record.id])

  const primaryKey = contactKey(primary.ref)
  const contacts = [
    primary,
    ...parties.filter((party) => contactKey(party.ref) !== primaryKey)
  ]
  return { contacts, loading }
}
